import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, TrendingUp, Plus } from "lucide-react";
import React, { useState } from "react";
import { useGetBillingOverviewQuery, useGetPaymentMethodsQuery } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TopUp } from "@/components/pages/billing/TopUp";
import { PaymentMethods } from "@/components/pages/billing/PaymentMethods";
import { AutoTopUpSettings } from "@/components/pages/billing/AutoTopUpSettings";
import { BillingDetails } from "@/components/pages/billing/BillingDetails";
import { KYCVerification } from "@/components/pages/billing/KYCVerification";

export function BillingOverview() {
  const { data, isLoading, error } = useGetBillingOverviewQuery();
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useGetPaymentMethodsQuery();
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return <div>Error loading billing information</div>;
  }

  const { organisation } = data;
  const hasNoCard = !isLoadingPaymentMethods && (!paymentMethods || paymentMethods.length === 0);
  const isVerified = organisation.kycVerificationStatus === "VERIFIED";

  return (
    <div className="p-4 space-y-6">
      {hasNoCard && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You have no payment method on file. Add a card below to keep your subscription, top-ups, and services
            running.
          </AlertDescription>
        </Alert>
      )}

      {!isVerified && <KYCVerification />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Account Balance
          </CardTitle>
          <CardDescription>Current credits and usage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold">${organisation.balance?.toFixed(2) || "0.00"}</div>
            <Dialog open={isTopUpOpen} onOpenChange={setIsTopUpOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Funds
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogTitle>Add Credits</DialogTitle>
                <DialogDescription>Add funds to your account balance</DialogDescription>
                <TopUp onSuccess={() => setIsTopUpOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant={organisation.subscriptionStatus === "ACTIVE" ? "default" : "secondary"}>
              {organisation.subscriptionStatus}
            </Badge>
            {organisation.subscriptionStatus === "ACTIVE" && organisation.activePlan && (
              <Badge variant="outline">{organisation.activePlan}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <AutoTopUpSettings />

      <PaymentMethods />

      <BillingDetails />
      </div>

      {isVerified && <KYCVerification />}
    </div>
  );
}
